import React, { useCallback, useEffect, useState, forwardRef } from 'react';
import classNames from 'classnames';
import { useReactToPrint } from 'react-to-print';
import { useCookies } from 'react-cookie/es6';
import isEmpty from 'lodash/isEmpty';
import styles from './content.module.less';
import ShownWhen from './shown_when';
import ToggleContent from './toggle_content';

const initialStateFalse = {
  definitions: false,
  roles: false,
  dataProcessing: false,
  dataProcessingTerms: false,
  transfers: false,
  obligations: false,
  precedence: false,
  indemnity: false,
  severability: false,
};

const initialStateTrue = {
  definitions: true,
  roles: true,
  dataProcessing: true,
  dataProcessingTerms: true,
  transfers: true,
  obligations: true,
  precedence: true,
  indemnity: true,
  severability: true,
};

const Content = forwardRef(({ user, dpaStatus, acceptDate, dpaLastUpdate }, ref) => {
  const [showSection, setShowSection] = useState(initialStateFalse);
  const [shouldPrint, setShouldPrint] = useState(false);
  const [cookies] = useCookies([]);

  const missingUser = isEmpty(user) || !user?.email;
  const loginUrl = `${window.location.origin}/accounts/login/?next=${window.location.href}`;

  const handlePrint = useReactToPrint({
    content: () => ref.current,
    copyStyles: true,
    pageStyle: JSON.stringify(styles),
  });

  const showAccept = dpaStatus !== 'signed_latest';
  const signedBeforeUpdate = dpaStatus === 'signed_before_update';

  const handleSectionClick = useCallback(
    (section) => {
      setShowSection((prev) => ({ ...prev, [section]: !prev[section] }));
    },
    [setShowSection],
  );

  const handleAccept = useCallback(async () => {
    const url = `${window.location.href}update`;
    const csrftoken = cookies?.csrftoken_accounts || '';

    const headers = {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrftoken,
    };

    const result = await window.fetch(url, { method: 'POST', headers });
    const resultObject = await result.json();

    if (result.status === 201) {
      window.location.reload(true);
    }
  }, [cookies]);

  const handlePrintClick = useCallback(() => {
    setShowSection(initialStateTrue);
    setShouldPrint(true);
  }, [setShowSection, setShouldPrint]);

  useEffect(() => {
    if (shouldPrint) {
      handlePrint();

      setShouldPrint(false);
    } else {
      window.scrollTo(0, 0);
      setShowSection(initialStateFalse);
    }
  }, [shouldPrint]);

  return (
    <div className={styles.container} id="content" ref={ref}>
      <iframe id="ifmcontentstoprint" style={{ height: 0, width: 0, position: 'absolute' }} title="iframe"></iframe>

      <h1>
        DATA PROTECTION ADDENDUM <span className={styles.headerLastUpdate}>(Last Updated: {dpaLastUpdate})</span>
      </h1>
      <div className={styles.dprWordExport}>
        <p className={styles.basicRow}>
          {' '}
          This Data Protection Addendum ("<strong>Addendum</strong>" or “<strong>DPA</strong>”), dated the date You
          accept this Addendum by clicking “I Accept” at the bottom of this page (“Addendum Effective Date”), forms part
          of, as applicable, the Terms of Service, MSA or other separate written and mutually signed Services agreement
          ("<strong>Terms</strong>") between (i) Pipl Inc. ("<strong>Pipl</strong>") and (ii) You (“<strong>You</strong>
          “, “<strong>Your</strong>”, "<strong>Client</strong>" or “<strong>Customer</strong>”), each being a “
          <strong>Party</strong>” and together the “<strong>Parties</strong>”.
          <br />
          <br />
          The Parties hereby agree that the terms and conditions set out below shall be added/incorporated as an
          Addendum to the Terms and references in this Addendum to the Terms are to the Terms as amended by, and
          including, this Addendum.
        </p>

        <div className={styles.listingMain}>
          <div className={styles.marginLeft15}>
            <ToggleContent
              handleSectionClick={() => handleSectionClick('definitions')}
              text="Definitions"
              number="1."
              shouldShow={showSection?.definitions}
            >
              <div className={styles.listingChild1}>
                <div className={styles.marginLeft35}>
                  1.1
                  <span>
                    In this Addendum, the following terms shall have the meanings set out below and cognate terms shall
                    be construed accordingly:
                  </span>
                  <ul className={styles.listingChild2}>
                    <li>
                      <span className={styles.threeLine}>(a)</span> <strong>"Affiliate"</strong> means an entity that
                      owns or controls, is owned or controlled by or is or under common control or ownership with either
                      Client or Pipl (as the context allows), where control is defined as the possession, directly or
                      indirectly, of the power to direct or cause the direction of the management and policies of an
                      entity, whether through ownership of voting securities, by contract or otherwise;
                    </li>

                    <li>
                      <span className={styles.threeLine}>(b)</span>
                      <strong>"Approved Jurisdiction"</strong> means a member state of the European Economic Area, or
                      other jurisdiction as may be approved as having adequate legal protections for data by the
                      European Commission, as found here:
                      <a
                        href="https://ec.europa.eu/info/law/law-topic/data-protection/international-dimension-data-protection/adequacy-decisions_en"
                        target="_blank"
                      >
                        https://ec.europa.eu/info/law/law-topic/data-protection/international-dimension-data-protection/adequacy-decisions_en
                      </a>
                      .
                    </li>

                    <li>
                      <span className={styles.fourLine}>(c)</span>
                      <strong>"Client Personal Data"</strong> means any Personal Data Processed by Pipl (i) on behalf of
                      Client (including for the sake of clarity, any Client Affiliate), or (ii) otherwise Processed by
                      Pipl, in each case pursuant to or in connection with instructions given by Client in writing,
                      consistent with the Terms;
                    </li>

                    <li>
                      <span className={styles.sixLine}>(d)</span>
                      <strong>"Standard Contractual Clauses"</strong> mean: the applicable module of the standard
                      contractual clauses for the transfer of personal data to third countries pursuant to Regulation
                      (EU) 2016/679 of the European Parliament and of the Council; from June 4th 2021, as available
                      here:
                      <a
                        href="https://eur-lex.europa.eu/eli/dec_impl/2021/914/oj?uri=CELEX:32021D0914&amp;locale=en"
                        target="_blank"
                      >
                        https://eur-lex.europa.eu/eli/dec_impl/2021/914/oj?uri=CELEX:32021D0914&amp;locale=en
                      </a>
                      , subject to any amendments contained in <strong>Annex 4</strong>.
                    </li>

                    <li>
                      <span className={styles.fiveLine}>(e)</span>
                      <strong>"Data Protection Laws"</strong> means, as applicable: (i) Regulation (EU) 2016/679{' '}
                      <strong>("GDPR")</strong>
                      together with applicable legislation implementing or supplementing the same or otherwise relating
                      to the processing of Personal Data of natural persons; (ii) Brazilian Federal Law no. 13,709/2018
                      – General Data Protection Law <strong>(“LGPD”)</strong>, (iii) the Data Protection Act 2018 of the
                      United Kingdom, as amended from time to time, and the GDPR as amended and incorporated into UK law
                      ("<strong>UK GDPR</strong>"), and (iv) the California Consumer Privacy Act, Cal. Civ. Code §
                      1798.100 et seq. ("<strong>CCPA</strong>");
                    </li>

                    <li>
                      <span>(f)</span>
                      <strong>"Services"</strong> means the services to be supplied by Pipl to Client or Client
                      Affiliates pursuant to the Terms.
                    </li>
                  </ul>
                </div>

                <div className={styles.threeLineTxt}>
                  <p className={styles.marginRight}>1.2</p>
                  <div>
                    The terms "<strong>Controller</strong>", "<strong>Data Subject</strong>", "
                    <strong>Personal Data</strong>", "<strong>Personal Data Breach</strong>", "<strong>Process</strong>
                    ", "<strong>Processor</strong>" and “<strong>Supervisory Authority</strong>” have the same meanings
                    as described in applicable Data Protection Laws, as applicable to the relationship between Pipl and
                    Client, and cognate terms shall be construed accordingly. Where applicable, "Controller" shall be
                    deemed to be a "Business" and "Processor" shall be deemed to be the "Service Provider", as these
                    terms are defined under the CCPA.
                  </div>
                </div>

                <div className={styles.threeLineTxt}>
                  1.3 Capitalized terms not otherwise defined in this Addendum shall have the meanings ascribed to them
                  in the Terms.
                </div>
              </div>
            </ToggleContent>
          </div>

          <div className={styles.marginLeft15}>
            <ToggleContent
              handleSectionClick={() => handleSectionClick('roles')}
              text="Roles of the Parties"
              number="2."
              shouldShow={showSection?.roles}
            >
              <p className={classNames(styles.marginLeft1, styles.marginBottom)}>
                The Parties acknowledge and agree that with regard to the Processing of Client Personal Data, and as
                more fully described in Annex 1 hereto, Client acts as a Controller or Processor, and Pipl acts as a
                Processor or Other Processor (as defined in section 4.2.8 below).
              </p>
              <p className={classNames(styles.marginLeft1, styles.marginBottom)}>
                The Parties expressly agree that Client shall be solely responsible for ensuring timely communications
                to Client’s Affiliates or the relevant Controller(s) who receive the Services, insofar as such
                communications may be required or useful in light of applicable Data Protection Laws to enable Client’s
                Affiliates or the relevant Controller(s) to comply with such Laws.
              </p>
            </ToggleContent>
          </div>

          <div className={styles.marginLeft15}>
            <ToggleContent
              handleSectionClick={() => handleSectionClick('dataProcessing')}
              text="Description of Personal Data Processing"
              number="3."
              shouldShow={showSection?.dataProcessing}
            >
              <p className={classNames(styles.marginLeft1, styles.marginBottom)}>
                In <strong>Annex 1</strong> to this Addendum, the Parties have mutually set out their understanding of
                the details of the Processing of the Client Personal Data to be Processed by Pipl pursuant to this
                Addendum.
              </p>
            </ToggleContent>
          </div>

          <div className={styles.marginLeft15}>
            <ToggleContent
              handleSectionClick={() => handleSectionClick('dataProcessingTerms')}
              text="Data Processing Terms"
              number="4."
              shouldShow={showSection?.dataProcessingTerms}
            >
              <div className={styles.listingChild1}>
                <div className={classNames(styles.fourLineTxt, styles.terms)}>
                  <strong>4.1</strong>
                  <p className={classNames(styles.marginLeft05, styles.marginBottom)}>
                    {' '}
                    Client shall comply with all applicable Data Protection Laws in connection with the performance of
                    this Addendum. As between the Parties, Client shall be solely responsible for compliance with
                    applicable Data Protection Laws regarding (i) the collection of and transfer to Pipl of Client
                    Personal Data and (ii) Pipl’s Processing of Personal Data carried out on behalf of Client in
                    connection with the Services. Client agrees not to provide Pipl with any data concerning a natural
                    person’s health, religion or any special categories of data (such as sensitive personal data) as
                    defined in the applicable Data Protection Laws.
                  </p>
                </div>

                <div className={classNames(styles.fourLineTxt, styles.terms)}>
                  <strong>4.2</strong>
                  <p className={classNames(styles.marginLeft05, styles.marginBottom)}>
                    {' '}
                    Pipl shall comply with all applicable Data Protection Laws in the Processing of Client Personal Data
                    and Pipl shall:
                  </p>
                </div>

                <div className={styles.listingChild1Sub}>
                  <div className={classNames(styles.sevenLineTxt, styles.terms)}>
                    <strong className={styles.marginRight}>4.2.1</strong>
                    <p className={classNames(styles.marginLeft05, styles.marginBottom)}>
                      process the Client Personal Data relating to the categories of Data Subjects for the purposes of
                      the Terms and for the specific purposes in each case as set out in <strong>Annex 1</strong> to
                      this Addendum and otherwise solely on the documented instructions of Client, for the purposes of
                      providing the Services and as otherwise necessary to perform its obligations under the Terms
                      including with regard to transfers of Client Personal Data to a third country outside the country
                      where Data Subjects are located, unless Pipl is otherwise required to do so by law to which it is
                      subject (and in such case, Pipl shall inform Client of that legal requirement before processing,
                      unless that law prohibits such notification);
                    </p>
                  </div>

                  <div className={classNames(styles.sevenLineTxt, styles.terms)}>
                    <strong className={styles.marginRight}>4.2.2</strong>
                    <p className={classNames(styles.marginLeft05, styles.marginBottom)}>
                      ensure that persons authorized to process the Client Personal Data have committed themselves to
                      confidentiality or are under an appropriate statutory obligation of confidentiality;{' '}
                    </p>
                  </div>

                  <div className={classNames(styles.sevenLineTxt, styles.terms)}>
                    <strong className={styles.marginRight}>4.2.3</strong>
                    <p className={classNames(styles.marginLeft05, styles.marginBottom)}>
                      implement and maintain the technical and organizational measures set out in the Terms and, taking
                      into account the state of the art, the costs of implementation and the nature, scope, context and
                      purposes of Processing as well as the risk of varying likelihood and severity for the rights and
                      freedoms of natural persons, and implement any further appropriate technical and organizational
                      measures necessary to ensure a level of security appropriate to the risk of the Processing of
                      Client Personal Data in accordance with applicable Data Protection Laws:
                    </p>
                  </div>
                  <div className={classNames(styles.sevenLineTxt, styles.terms)}>
                    <strong className={styles.marginRight}>4.2.4</strong>
                    <p className={classNames(styles.marginLeft05, styles.marginBottom)}>
                      pseudonymization and encryption of Client Personal Data;
                    </p>
                  </div>
                  <div className={classNames(styles.sevenLineTxt, styles.terms)}>
                    <strong className={styles.marginRight}>4.2.5</strong>
                    <p className={classNames(styles.marginLeft05, styles.marginBottom)}>
                      ensuring ongoing confidentiality, integrity, availability and resilience of processing systems and
                      services that process Client Personal Data;
                    </p>
                  </div>
                  <div className={classNames(styles.sevenLineTxt, styles.terms)}>
                    <strong className={styles.marginRight}>4.2.6</strong>
                    <p className={classNames(styles.marginLeft05, styles.marginBottom)}>
                      restoring availability and access to Client Personal Data in a timely manner in the event of a
                      physical or technical incident; and
                    </p>
                  </div>
                  <div className={classNames(styles.sevenLineTxt, styles.terms)}>
                    <strong className={styles.marginRight}>4.2.7</strong>
                    <p className={classNames(styles.marginLeft05, styles.marginBottom)}>
                      regularly testing, assessing and evaluating the effectiveness of technical and organizational
                      measures for ensuring the security of the processing of the Client Personal Data.
                      <br />
                      <br />
                      Any amendment to such agreed measures that is necessitated by Client shall be dealt with via an
                      agreed change control process between Pipl and Client.
                    </p>
                  </div>

                  <div className={classNames(styles.sevenLineTxt, styles.terms)}>
                    <strong className={styles.marginRight}>4.2.8</strong>
                    <p className={classNames(styles.marginLeft05, styles.marginBottom)}>
                      Client (either in its own name, as a Controller, or on behalf of the relevant Controller(s), as
                      applicable), hereby expressly and specifically authorizes Pipl to engage another Processor to
                      Process the Client Personal Data ("<strong>Other Processor</strong>"), and specifically the Other
                      Processors listed in <strong>Annex 3</strong> hereto, subject to Pipl:
                    </p>
                  </div>
                  <div>
                    <div className={classNames(styles.listingChild21, styles.zeroPadding)}>
                      <div className={styles.subItem}>
                        <span>(a)</span>
                        <div>
                          notifying Client of any intended changes to its use of Other Processors listed in{' '}
                          <strong>Annex 3</strong> by emailing notice of the intended change to Client;
                        </div>
                      </div>

                      <div className={styles.subItem}>
                        <span>(b)</span>
                        including data protection obligations in its contract with each Other Processor that are
                        materially the same as those set out in this Addendum; and
                      </div>

                      <div className={styles.subItem}>
                        <span>(c)</span>
                        remaining liable to the Client for any failure by each Other Processor to fulfill its
                        obligations in relation to the Processing of the Client Personal Data in accordance with the
                        Terms.
                      </div>
                    </div>
                  </div>
                  <div className={classNames(styles.sevenLineTxt, styles.terms)}>
                    <strong className={styles.marginRight}>4.2.9</strong>
                    <p className={classNames(styles.marginLeft05, styles.marginBottom)}>
                      to the extent legally permissible, promptly notify Client of any communication from a Data Subject
                      regarding the Processing of Client Personal Data, or any other communication (including from a
                      Supervisory Authority) relating to any obligation under the applicable Data Protection Laws in
                      respect of the Client Personal Data and, taking into account the nature of the Processing, assist
                      Client (or the relevant Controller) by appropriate technical and organizational measures, insofar
                      as this is possible, for the fulfillment of Client’s, Client’s Affiliates’ or the relevant
                      Controller(s)’ obligation to respond to requests for exercising the data subject's rights laid
                      down in applicable Data Protection Laws;
                    </p>
                  </div>
                  <div className={classNames(styles.sevenLineTxt, styles.terms)}>
                    <strong className={styles.marginRight}>4.2.10</strong>
                    <p className={classNames(styles.marginBottom)}>
                      upon Pipl’s becoming aware of a Personal Data Breach involving Client Personal Data, notify Client
                      without undue delay, of any Personal Data Breach involving Client Personal Data, such notice to
                      include all information available to Pipl and reasonably required by Client (or the relevant
                      Controller) to comply with its obligations under the applicable Data Protection Laws;
                    </p>
                  </div>
                  <div className={classNames(styles.sevenLineTxt, styles.terms)}>
                    <strong className={styles.marginRight}>4.2.11</strong>
                    <p className={classNames(styles.marginBottom)}>
                      should Client request any assistance from Pipl so that it can comply with its obligations under
                      applicable Data Protection Laws and when any such assistance from Pipl goes beyond the standard
                      functionality of the Services, Pipl may charge Client for any costs beyond the agreed upon
                      consideration provided for under the Terms to the extent it is not commercially reasonable for
                      Pipl to provide such assistance without charge (considering relevant factors such as volume of
                      requests, complexity of instructions and timescale requested). This shall include, without
                      limitation, costs incurred by Pipl in executing Partner’s instructions relating to the provision
                      of access to Personal Data, any rectification or erasure of Personal Data, as well as any measures
                      deemed necessary by Partner in response to any Personal Data Breaches.
                    </p>
                  </div>
                  <div className={classNames(styles.sevenLineTxt, styles.terms)}>
                    <strong className={styles.marginRight}>4.2.12</strong>
                    <p className={classNames(styles.marginBottom)}>
                      cease Processing the Client Personal Data within no longer than fifteen (15) days following the
                      termination or expiry of the Terms, or following the receipt of an express written request in this
                      sense by Client, Client’s Affiliates or the relevant Controller(s), by deleting (including by
                      ensuring such data is in non-readable format) all copies of the Client Personal Data Processed by
                      Pipl, unless (and solely to the extent and for such period as) Pipl has legitimate reasons to
                      maintain any such Client Personal Data, including to comply with legal or regulatory obligations
                      to which it is subject or to allow Pipl to exert its rights in the context of a legal dispute
                      involving any such Client Personal Data. Notwithstanding the foregoing or anything to the contrary
                      contained herein, Pipl may retain Personal Data and shall have no obligation to return Personal
                      Data to the extent that any such retention of Personal Data is required by applicable laws or
                      regulations obligations. Any such Personal Data retained shall remain subject to the obligations
                      of confidentiality set forth in the Terms; and
                    </p>
                  </div>
                  <div className={classNames(styles.sevenLineTxt, styles.terms)}>
                    <strong className={styles.marginRight}>4.2.13</strong>
                    <p className={classNames(styles.marginBottom)}>
                      make available to Client all information necessary to demonstrate compliance with this Addendum
                      and allow for and contribute to audits, including inspections, by Client, or an auditor mandated
                      by Client. For the purposes of demonstrating compliance with this Addendum under this section
                      4.2.13, the Parties agree that once per year during the term of the Terms, at the request of the
                      Client, Pipl will provide to Client, on reasonable notice, responses to cybersecurity and other
                      assessments relating to the protection of Personal Data. Client agrees to pay Pipl for time and
                      for out of pocket expenses incurred by Pipl in connection with assistance provided in connection
                      with such audits, responses to cybersecurity and other assessments.
                    </p>
                  </div>
                </div>

                <div className={classNames(styles.fourLineTxt, styles.terms)}>
                  <strong>4.3</strong>
                  <p className={classNames(styles.marginLeft05, styles.marginBottom)}>
                    {' '}
                    Notwithstanding anything to the contrary in the DPA, Client acknowledges that Pipl shall have the
                    right to collect, use and disclose data:
                  </p>
                </div>

                <div className={styles.listingChild1Sub}>
                  <div className={classNames(styles.sevenLineTxt, styles.terms)}>
                    <strong className={styles.marginRight}>4.3.1</strong>
                    <p className={classNames(styles.marginLeft05, styles.marginBottom)}>
                      Collected in the context of providing the Services, for the purpose of the operation, support or
                      use of its services for the purpose of administrating the business and/or contractual relationship
                      with the Client, including for billing, audit and recordkeeping purposes and other legitimate
                      business purposes, such as account management, technical support, troubleshooting, security,
                      protecting against fraudulent or illegal activity, and for the purpose of establishment/exercise
                      and defense of legal claims.
                    </p>
                  </div>

                  <div className={classNames(styles.sevenLineTxt, styles.terms)}>
                    <strong className={styles.marginRight}>4.3.2</strong>
                    <p className={classNames(styles.marginLeft05, styles.marginBottom)}>
                      Collected in the context of using the Services, for the purpose of analytics, market research,
                      product improvement and development, provided however that the foregoing shall be based solely on
                      the processing of aggregated and/or anonymized information.
                    </p>
                  </div>
                </div>

                <div className={classNames(styles.fourLineTxt, styles.terms)}>
                  <strong>4.4</strong>
                  <p className={classNames(styles.marginLeft05, styles.marginBottom)}>
                    To the extent that any data referred under section 4.3 is considered Personal Data, then Pipl shall
                    be regarded as an independent Controller of such data under applicable Data Protection Laws and its
                    processing by Pipl shall be outside the scope of this DPA.
                  </p>
                </div>
              </div>
            </ToggleContent>

            <div className={styles.clear}></div>
          </div>
          <div className={styles.clear}></div>

          <div className={styles.marginLeft15}>
            <ToggleContent
              handleSectionClick={() => handleSectionClick('transfers')}
              text="Transfers"
              number="5."
              shouldShow={showSection?.transfers}
            >
              <div className={styles.marginLeft1}>
                <div className={styles.flex}>
                  <span className={styles.marginRight}>5.1</span>{' '}
                  <span>
                    Client acknowledges and agrees that Pipl will, both on its own and through its Other Processors,
                    Process Client Personal Data in servers located in the United States of America. The Client Personal
                    Data is being transferred in accordance with the applicable Data Protection Laws. Accordingly, where
                    applicable, the Parties shall deemed to enter into the Standard Contractual Clauses, which is
                    incorporated herein by reference, and Client shall be deemed as the data exporter and Pipl shall be
                    deemed as the data importer (as these terms are defined therein).
                  </span>
                </div>
                <div className={styles.flex}>
                  <span className={styles.marginRight}>5.2</span>
                  <span>
                    To the extent that the LGPD applies, the Parties acknowledge that for the purposes of enabling
                    lawful transfers of Client Personal Data outside Brazil in accordance with the applicable Data
                    Protection Laws, specific regulations issued by the local Supervisory Authority must be issued and
                    that, until any such new regulations arise, Client will consider the terms of this Addendum as
                    sufficient grounds for enabling the transfers of Client Personal Data to the United States of
                    America, particularly the provisions set out below referring to the Standard Contractual Clauses.
                    The Parties undertake to engage in a good faith dialogue to jointly determine a new data transfer
                    mechanism to carry out the purposes of the Terms in the event that any such new regulations arise
                    under applicable Data Protection Laws. Pipl acts as a Processor with respect to Personal Data
                    received pursuant to a data transfer covered hereunder.
                  </span>
                </div>
                <div className={styles.flex}>
                  <span className={styles.marginRight}>5.3</span>
                  <div>
                    <div>
                      Pipl may transfer Personal Data of residents of the EEA outside the EEA (&quot;Transfer&quot;),
                      only subject to the following:
                    </div>
                    <div>
                      <div>
                        <span className={styles.marginRight}>(A)</span>
                        <span>
                          the transfer is necessary for the purpose of Pipl carrying out its obligations under the
                          Agreement, or is required under applicable laws; and
                        </span>
                      </div>
                      <div>
                        <span className={styles.marginRight}>(B)</span>
                        <span>
                          the transfer is done: (i) to an Approved Jurisdiction, or (ii) subject to appropriate
                          safeguards (for example, through the use of the Standard Contractual Clauses, or other
                          applicable frameworks), or (iii) in accordance with any of the exceptions listed in the Data
                          Protection Laws (in which event Client will inform Pipl which exception applies to each
                          transfer and will assume complete and sole liability to ensure that the exception applies).
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.flex}>
                  <span className={styles.marginRight}>5.4</span>
                  <span>
                    Appendix 1 and Appendix 2 to the Standard Contractual Clauses shall be deemed to be prepopulated
                    with the relevant sections of <strong>Annex 1</strong> and <strong>Annex 2</strong> (respectively)
                    to this Addendum and the processing operations are deemed to be those described in the Terms.
                    <br />
                    <br />
                  </span>
                </div>
              </div>
            </ToggleContent>
          </div>
          <div className={styles.pagebreak}> </div>
          <div className={styles.marginLeft15}>
            <ToggleContent
              handleSectionClick={() => handleSectionClick('obligations')}
              text="Obligations under the CCPA (to the extent applicable)"
              number="6."
              shouldShow={showSection?.obligations}
            >
              <br />
              <p className={classNames(styles.marginLeft1, styles.marginBottom)}>
                To the extent that the CCPA applies, Pipl shall be deemed as the Service Provider of the Client, and
                accordingly:
              </p>
              <div className={styles.marginLeft1}>
                <div className={styles.flex}>
                  <span className={styles.marginRight}>6.1</span>{' '}
                  <span>Pipl shall not Sell Client Personal Data (as the term "Sell") is defined under the CCPA.</span>
                </div>
                <div className={styles.flex}>
                  <span className={styles.marginRight}>6.2</span>
                  <span>
                    Pipl is prohibited from retaining, using, or disclosing Client Personal Data for commercial purpose
                    other than providing the Services to Client under the Terms and from retaining, using, or disclosing
                    Personal Data outside of the Terms.
                  </span>
                </div>
                <div className={styles.flex}>
                  <span className={styles.marginRight}>6.3</span>
                  <span>Pipl understands its obligations under this Clause 6 and will comply with them.</span>
                </div>
              </div>
              <br />
            </ToggleContent>
          </div>

          <div className={styles.marginLeft15}>
            <ToggleContent
              handleSectionClick={() => handleSectionClick('precedence')}
              text="Precedence"
              number="7."
              shouldShow={showSection?.precedence}
            >
              <p className={classNames(styles.marginLeft1, styles.marginBottom)}>
                The provisions of this Addendum are supplemental to the provisions of the Terms. In the event of any
                inconsistency between the provisions of this Addendum and the provisions of the Terms, the provisions of
                this Addendum shall prevail.
              </p>
            </ToggleContent>
          </div>
          <div className={styles.marginLeft15}>
            <ToggleContent
              handleSectionClick={() => handleSectionClick('indemnity')}
              text="Indemnity"
              number="8."
              shouldShow={showSection?.indemnity}
            >
              <p className={classNames(styles.marginLeft1, styles.marginBottom)}>
                To the extent permissible by law, Client shall indemnify and hold harmless Pipl against all (i) losses,
                (ii) third party claims, (iii) administrative fines and (iv) costs and expenses (including, without
                limitation, reasonable legal, investigatory and consultancy fees and expenses) reasonably incurred in
                relation to (i), (ii) or iii), suffered by Pipl and that arise from any breach by Client of this
                Addendum or of its obligations under applicable Data Protection Laws.
              </p>
            </ToggleContent>
          </div>
          <div className={styles.marginLeft15}>
            <ToggleContent
              handleSectionClick={() => handleSectionClick('severability')}
              text="Severability"
              number="9."
              shouldShow={showSection?.severability}
            >
              <p className={classNames(styles.marginLeft1, styles.marginBottom)}>
                The Parties agree that, if any section or sub-section of this Addendum is held by any court or competent
                authority to be unlawful or unenforceable, it shall not invalidate or render unenforceable any other
                section of this Addendum.
              </p>
            </ToggleContent>
          </div>
        </div>

        <div id="dpa-accept" className={classNames(styles.cDpaAcceptForm, styles.textCenter)}>
          <ShownWhen condition={signedBeforeUpdate}>
            <div className={styles.cDpaAcceptText}>
              <b>
                You previously accepted the older version of DPA on {acceptDate}, which is still valid. However, if You
                would like to accept this updated DPA version please click on accept
              </b>
            </div>
          </ShownWhen>

          <ShownWhen condition={showAccept}>
            <p className={classNames(styles.marginBottom, styles.printHide)}>
              By clicking “I ACCEPT” below, this Addendum is entered into and becomes a binding part of the Terms with
              effect from the Addendum Effective Date.
            </p>

            <ShownWhen condition={missingUser}>
              <p className={styles.printHide}>
                Please login in order to accept <a href={loginUrl}>login</a>
              </p>
            </ShownWhen>
            <div className={classNames(styles.rowContainer, styles.printHide)}>
              <button
                disabled={missingUser}
                className={classNames(styles.acceptButton, {
                  [styles.disabled]: missingUser,
                })}
                type="button"
                onClick={handleAccept}
              >
                I Accept
              </button>
              <a href="/" className={styles.customButton}>
                Cancel
              </a>
            </div>
          </ShownWhen>

          <ShownWhen condition={!showAccept}>
            <div className={classNames(styles.cDpaAcceptText, styles.rowContainer, styles.printHide)}>
              <b>Accepted {acceptDate}</b>
            </div>
          </ShownWhen>
        </div>

        <div className={classNames(styles.rowContainer, styles.printHide)}>
          <button
            type="button"
            className={classNames(styles.customButton, styles.printButton)}
            onClick={handlePrintClick}
          >
            Print
          </button>
        </div>

        <div className={classNames(styles.section2, styles.printHide)}>
          <h5 className={styles.underline}>Annex section</h5>
          <br />
          <p className={classNames(styles.marginBottom)}>
            <a href="https://pipl.com/dpa/annexes" target="_blank">
              DPA Annexes 1-4
            </a>
          </p>
        </div>
      </div>
    </div>
  );
});

export default Content;
